/**
 * UtilizationChart
 *
 * Four series, because "how busy are we" and "where did the time go" are
 * different questions:
 *   Utilised  shaded area - booked days as a share of sellable capacity
 *   Site      of that, the days spent on a customer site
 *   Office    of that, the days spent offline / in the office
 *   PTO       time off, as a share of ALL days (it is not sellable capacity,
 *             so it is deliberately measured against a different denominator)
 *
 * Every series prints its own number above each month. There is no tooltip:
 * once the figures are on the chart a hover box only covers the thing you are
 * trying to read.
 *
 * Site + Office always add up to Utilised. PTO does not, by design.
 * Data comes from the Manpower calendar - see utils/utilizationFromCalendar.
 */

import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LabelList,
  ResponsiveContainer,
  ReferenceLine,
  usePlotArea,
  useYAxisDomain,
} from 'recharts';
import { stackLabels } from './stackLabels';

const UTIL = { key: 'util', color: '#1B5E20' };
const SERIES = [
  { key: 'site', name: 'Site', color: '#1565C0' },
  { key: 'office', name: 'Office', color: '#6A1B9A' },
  { key: 'pto', name: 'PTO', color: '#B07503' },
];
// Bottom-to-top order for TIES: stackLabels keeps the first of two equal
// values in place and lifts the next, so Utilised - the headline number -
// goes last and ends up on top when it ties with Site (everyone on site).
const LABELLED = [...SERIES].reverse().concat(UTIL);

const LIFT = 7; // px from a point up to its label's baseline
const GAP = 11; // px between two stacked baselines: 9-10px bold text + air

/**
 * Every series' number for ONE month, stacked so none of them touch.
 *
 * It used to be four independent LabelLists, one per series. They cannot see
 * each other, so close values printed on top of one another, and the ones
 * pushed below their line landed on the month names. This is rendered as the
 * content of a single LabelList (on an invisible Line drawn last) so that one
 * component places the whole column: recharts hands it the month's x, the plot
 * area gives the value->pixel mapping, and stackLabels spaces them.
 */
function MonthLabels({ x, index, rows }) {
  const plot = usePlotArea();
  const domain = useYAxisDomain();
  const row = rows && rows[index];
  if (!plot || !row || !Number.isFinite(x)) return null;

  const max = Number(domain && domain[1]) || 110;
  const yOf = (v) => plot.y + plot.height * (1 - v / max);
  const floor = plot.y + plot.height - 3; // never below the axis line

  const vals = LABELLED.map((s) => ({ ...s, v: Number(row[s.key]) || 0 }));
  // Four "0%" in a pile say less than one. An empty month gets a single label.
  const items = vals.every((s) => s.v === 0) ? [{ key: 'empty', color: '#9E9E9E', v: 0 }] : vals;

  const placed = stackLabels(
    items.map((s) => ({ ...s, y: Math.min(yOf(s.v) - LIFT, floor) })),
    { gap: GAP, top: plot.y - 4 }
  );

  return (
    <g className="util-labels">
      {placed.map((s) => (
        <text
          key={s.key}
          x={x}
          y={s.y}
          textAnchor="middle"
          className="util-label"
          data-series={s.key}
          fill={s.color}
          fontFamily="Montserrat"
          fontSize={s.key === 'util' ? 10 : 9}
          fontWeight={s.key === 'util' ? 800 : 700}
          // white halo: a label that sits across a line stays legible
          stroke="#fff"
          strokeWidth={3}
          strokeLinejoin="round"
          paintOrder="stroke"
        >{`${s.v}%`}</text>
      ))}
    </g>
  );
}

export const UtilizationChart = ({ data = [] }) => (
  <ResponsiveContainer width="100%" height="100%">
    {/* Right margin: the "85% target" tag sits past the last point. Top margin:
        headroom for a label column stacked above a month near 100%. */}
    <ComposedChart data={data} margin={{ top: 14, right: 56, left: 0, bottom: 0 }}>
      <defs>
        <linearGradient id="utilGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#1B5E20" stopOpacity={0.15} />
          <stop offset="95%" stopColor="#1B5E20" stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
      <XAxis
        dataKey="week"
        // Inset the first and last month so their labels clear the Y axis
        // ticks on the left and the card edge on the right.
        padding={{ left: 28, right: 28 }}
        tick={{ fontSize: 10, fill: '#888', fontFamily: 'Montserrat', fontWeight: 700 }}
        tickMargin={6}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        domain={[0, 110]}
        ticks={[0, 25, 50, 75, 100]}
        tick={{ fontSize: 9, fill: '#ccc', fontFamily: 'Montserrat' }}
        axisLine={false}
        tickLine={false}
        width={30}
        tickFormatter={(v) => `${v}%`}
      />
      <ReferenceLine
        y={85}
        stroke="#E65100"
        strokeDasharray="4 4"
        strokeWidth={1}
        label={{ value: '85% target', position: 'right', fill: '#E65100', fontSize: 8, fontFamily: 'Montserrat', fontWeight: 700 }}
      />
      <Legend
        verticalAlign="top"
        height={26}
        iconType="plainline"
        iconSize={14}
        wrapperStyle={{ fontFamily: 'Montserrat', fontSize: 10, fontWeight: 700 }}
      />
      <Area
        type="monotone"
        dataKey="util"
        name="Utilised"
        stroke="#1B5E20"
        strokeWidth={2.5}
        fill="url(#utilGrad)"
        dot={{ r: 4, fill: '#1B5E20', strokeWidth: 2, stroke: '#fff' }}
        activeDot={{ r: 6 }}
      />
      {/* The breakdown rides on top as plain lines, so the shaded area stays
          the headline number and the split reads as detail under it. */}
      {SERIES.map((s) => (
        <Line
          key={s.key}
          type="monotone"
          dataKey={s.key}
          name={s.name}
          stroke={s.color}
          strokeWidth={1.75}
          strokeDasharray={s.key === 'pto' ? '4 3' : undefined}
          dot={{ r: 2.5, fill: s.color, strokeWidth: 0 }}
          activeDot={{ r: 4 }}
        />
      ))}
      {/* Drawn last so every number sits above every line. It hangs off an
          invisible Line purely to borrow recharts' x for each month. */}
      <Line dataKey="util" stroke="none" dot={false} activeDot={false} legendType="none" isAnimationActive={false}>
        <LabelList dataKey="util" content={<MonthLabels rows={data} />} />
      </Line>
    </ComposedChart>
  </ResponsiveContainer>
);
